import Vue from 'vue';
import Vuex from 'vuex';
import test from './test';
import checkup from './checkup';
import questionnaire from './questionnaire';
import strategy from './strategy';
import drug from './drug';
import health from './health';
import CoreApi from '../api/core';
import User from '../models/user';
import Client from '../models/client';
import Note from '../models/note';
import Conclusion from '../models/conclusion';
import Functional from '../models/functional';
import FunctionalEstablishment from '../models/functionalEstablishment';
import Diagnosis from '../models/diagnosis';
import DiagnosisEstablishment from '../models/diagnosisEstablishment';
import Anamnesis from '../models/anamnesis';

Vue.use(Vuex);

const env = process.env;
const coreApi = new CoreApi(
  env.VUE_APP_CLINIC_API_CORE_URL,
  env.VUE_APP_CLINIC_API_CORE_TIMEOUT
);

const state = {
  token: localStorage.token || '',
  user: null,
  clients: [],
  notes: {},
  conclusions: {},
  anamneses: {},
  functionals: [],
  functionalEstablishments: {},
  diagnoses: [],
  diagnosisEstablishments: {},
};

const getters = {
  isAuthorized: state => {
    return state.user instanceof User;
  },

  hasToken: state => {
    return typeof state.token === 'string' && state.token.trim() !== '';
  },

  clientNotes: state => clientId => {
    if (!state.notes[clientId]) {
      return [];
    }

    return state.notes[clientId];
  },

  clientConclusions: state => clientId => {
    if (!state.conclusions[clientId]) {
      return [];
    }

    return state.conclusions[clientId];
  },

  clientAnamneses: state => clientId => {
    if (!state.anamneses[clientId]) {
      return [];
    }

    return state.anamneses[clientId];
  },

  clientFunctionalEstablishments: state => clientId => {
    if (!state.functionalEstablishments[clientId]) {
      return [];
    }

    return state.functionalEstablishments[clientId];
  },

  clientDiagnosisEstablishments: state => clientId => {
    if (!state.diagnosisEstablishments[clientId]) {
      return [];
    }

    return state.diagnosisEstablishments[clientId];
  },
};

const mutations = {
  SET_USER(state, data) {
    if (typeof data !== 'undefined' && typeof data.image !== 'undefined') {
      data.image = `${env.VUE_APP_CLINIC_API_CORE_URL}${data.image.url}`;
    }

    state.user = new User(data);
    state.token = state.user.token;

    localStorage.token = state.token;
  },

  UNSET_USER(state, data) {
    state.user = null;
    state.token = '';

    localStorage.token = state.token;
  },

  SET_CLIENTS: (state, payload) => {
    const clients = [];

    for (const client of payload) {
      if (typeof client.image !== 'undefined') {
        client.image = `${env.VUE_APP_CLINIC_API_CORE_URL}${client.image.url}`;
      }

      clients.push(new Client(client));
    }

    state.clients = clients;
  },

  SET_NOTES: (state, payload) => {
    state.notes[payload.clientId] = payload.items.map(item => new Note(item));
    state.notes = Object.assign({}, state.notes);
  },

  SET_CONCLUSIONS: (state, payload) => {
    state.conclusions[payload.clientId] = payload.items
      .map(item => new Conclusion(item));
    state.conclusions = Object.assign({}, state.conclusions);
  },

  SET_FUNCTIONALS: (state, payload) => {
    state.functionals = payload.map(item => new Functional(item));
  },

  SET_FUNCTIONAL_ESTABLISHMENTS: (state, payload) => {
    state.functionalEstablishments[payload.clientId] = payload.items
      .map(item => new FunctionalEstablishment(item));
    state.functionalEstablishments = Object.assign(
      {},
      state.functionalEstablishments
    );
  },

  SET_DIAGNOSES: (state, payload) => {
    state.diagnoses = payload.map(item => new Diagnosis(item));
  },

  SET_DIAGNOSIS_ESTABLISHMENTS: (state, payload) => {
    state.diagnosisEstablishments[payload.clientId] = payload.items
      .map(item => new DiagnosisEstablishment(item));
    state.diagnosisEstablishments = Object.assign(
      {},
      state.diagnosisEstablishments
    );
  },

  SET_ANAMNESES: (state, payload) => {
    state.anamneses[payload.clientId] = payload.items
      .map(item => new Anamnesis(item));
    state.anamneses = Object.assign({}, state.anamneses);
  },

  SET_STRATEGIES: (state, payload) => {
    state.strategies = payload.items;
  },

  SET_STAGES: (state, payload) => {
    state.stages[payload.clientId] = payload.items;
    state.stages = Object.assign({}, state.stages);
  },
};

const actions = {
  LOG_IN: async (context, payload) => {
    const {login, password} = payload;
    const data = await coreApi.logIn(login, password);

    context.commit('SET_USER', data);
  },

  LOG_OUT: async (context) => {
    const token = context.state.token;

    context.commit('UNSET_USER', null);

    await coreApi.logOut(token);
  },

  AUTH: async (context) => {
    const data = await coreApi.getProfile(context.state.token);

    context.commit('SET_USER', data);
  },

  LOAD_CLIENTS: async (context) => {
    const data = await coreApi.getClients(context.state.token);

    context.commit('SET_CLIENTS', data.items);
  },

  LOAD_NOTES: async (context, payload) => {
    const token = context.rootState.token;
    const result = await coreApi.getNotes(token, payload);
    const data = { items: result.items, clientId: payload };

    context.commit('SET_NOTES', data);
  },

  ADD_NOTE: async (context, payload) => {
    const token = context.rootState.token;
    const { client, text } = payload;

    await coreApi.addNote(token, client, text);

    await context.dispatch('LOAD_NOTES', client);
  },

  UPDATE_NOTE: async (context, payload) => {
    const token = context.rootState.token;
    const { id, text, client } = payload;

    await coreApi.updateNote(token, id, text);

    await context.dispatch('LOAD_NOTES', client);
  },

  LOAD_CONCLUSIONS: async (context, payload) => {
    const token = context.rootState.token;
    const result = await coreApi.getConclusions(token, payload);
    const data = { items: result, clientId: payload };

    context.commit('SET_CONCLUSIONS', data);
  },

  ADD_CONCLUSION: async (context, payload) => {
    const token = context.rootState.token;
    const { client, text, note, dateFrom } = payload;

    await coreApi.addConclusion(token, { user: client, text, note, dateFrom });

    await context.dispatch('LOAD_CONCLUSIONS', client);
  },

  UPDATE_CONCLUSION: async (context, payload) => {
    const token = context.rootState.token;
    const { id, client, text, note, dateFrom } = payload;

    await coreApi.updateConclusion(token, id, { text, note, dateFrom });

    await context.dispatch('LOAD_CONCLUSIONS', client);
  },

  LOAD_FUNCTIONALS: async (context, payload) => {
    const token = context.rootState.token;
    const result = await coreApi.getFunctionals(token);

    context.commit('SET_FUNCTIONALS', result);
  },

  LOAD_FUNCTIONAL_ESTABLISHMENTS: async (context, payload) => {
    const token = context.rootState.token;
    const result = await coreApi.getFunctionalEstablishments(token, payload);
    const data = { items: result, clientId: payload };

    context.commit('SET_FUNCTIONAL_ESTABLISHMENTS', data);
  },

  ADD_FUNCTIONAL_ESTABLISHMENT: async (context, payload) => {
    const token = context.rootState.token;
    const { client, functional, note, dateFrom, dateTo } = payload;
    const data = { user: client, functional, note, dateFrom, dateTo };

    await coreApi.addFunctionalEstablishment(token, data);

    await context.dispatch('LOAD_FUNCTIONAL_ESTABLISHMENTS', client);
  },

  UPDATE_FUNCTIONAL_ESTABLISHMENT: async (context, payload) => {
    const token = context.rootState.token;
    const { id, client, note, dateFrom, dateTo } = payload;
    const data = { note, dateFrom, dateTo };

    await coreApi.updateFunctionalEstablishment(token, id, data);

    await context.dispatch('LOAD_FUNCTIONAL_ESTABLISHMENTS', client);
  },

  LOAD_DIAGNOSES: async (context, payload) => {
    const token = context.rootState.token;
    const result = await coreApi.getDiagnoses(token);

    context.commit('SET_DIAGNOSES', result);
  },

  LOAD_DIAGNOSIS_ESTABLISHMENTS: async (context, payload) => {
    const token = context.rootState.token;
    const result = await coreApi.getDiagnosisEstablishments(token, payload);
    const data = { items: result, clientId: payload };

    context.commit('SET_DIAGNOSIS_ESTABLISHMENTS', data);
  },

  ADD_DIAGNOSIS_ESTABLISHMENT: async (context, payload) => {
    const token = context.rootState.token;
    const { client, diagnosis, note, dateFrom, dateTo } = payload;
    const data = { user: client, diagnosis, note, dateFrom, dateTo };

    await coreApi.addDiagnosisEstablishment(token, data);

    await context.dispatch('LOAD_DIAGNOSIS_ESTABLISHMENTS', client);
  },

  UPDATE_DIAGNOSIS_ESTABLISHMENT: async (context, payload) => {
    const token = context.rootState.token;
    const { id, client, note, dateFrom, dateTo } = payload;
    const data = { note, dateFrom, dateTo };

    await coreApi.updateDiagnosisEstablishment(token, id, data);

    await context.dispatch('LOAD_DIAGNOSIS_ESTABLISHMENTS', client);
  },

  LOAD_ANAMNESES: async (context, payload) => {
    const token = context.rootState.token;
    const result = await coreApi.getAnamneses(token, payload);
    const data = { items: result, clientId: payload };

    context.commit('SET_ANAMNESES', data);
  },

  ADD_ANAMNESIS: async (context, payload) => {
    const token = context.rootState.token;
    const { user, text, date } = payload;

    await coreApi.addAnamnesis(token, { user, text, date });

    await context.dispatch('LOAD_ANAMNESES', user);
  },

  UPDATE_ANAMNESIS: async (context, payload) => {
    const token = context.rootState.token;
    const { id, user, text, date } = payload;

    await coreApi.updateAnamnesis(token, id, { text, date });

    await context.dispatch('LOAD_ANAMNESES', user);
  },
};

export default new Vuex.Store({
  state,
  getters,
  mutations,
  actions,
  modules: {
    test,
    checkup,
    questionnaire,
    strategy,
    drug,
    health,
  },
});
